import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleDot,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  History,
  LayoutGrid,
  List,
  MapPin,
  Pencil,
  Search,
  Sparkles,
  Trash2,
  Users,
  XCircle,
} from "lucide-react";
import ListingAuditDrawer from "@/components/ListingAuditDrawer";
import { ComingSoonDeals } from "@/components/ComingSoonDeals";
import { MarketExplorer } from "@/components/MarketExplorer";
import type { ListingMapPoint } from "@/components/ListingsMap";
import { loadMapPoints } from "@/lib/mapPoints";
import { TrendingSection, type TrendingListing } from "@/components/TrendingCarousel";
import { HeroCarousel, featureBanners } from "@/components/HeroCarousel";
import { CategoryTiles } from "@/components/CategoryTiles";

import type { BusinessListing, User as UserType } from "@/types";
import { api, assetUrl } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { cn } from "@/lib/utils";
import {
  ListingFilters,
  buildListingFilterParams,
  readStoredFilters,
  EMPTY_FILTERS,
  type ListingFilterValues,
} from "@/components/ListingFilters";
import ListingHotBadge from "@/components/ListingHotBadge";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";
import { useAuth } from "@/contexts/AuthContext";
import { canActOnListingOrg } from "@/lib/roles";

interface LookupItem {
  id: number;
  name: string;
}

const LISTING_STATUSES: { id: number; name: string; label: string }[] = [
  { id: 1, name: "draft", label: "Draft" },
  { id: 2, name: "under_review", label: "Under Review" },
  { id: 3, name: "active", label: "Active" },
  { id: 9, name: "loi", label: "LOI" },
  { id: 4, name: "under_offer", label: "Under Offer" },
  { id: 10, name: "due_diligence", label: "Due Diligence" },
  { id: 11, name: "escrow", label: "Escrow" },
  { id: 5, name: "sold", label: "Sold" },
  { id: 6, name: "expired", label: "Expired" },
  { id: 7, name: "withdrawn", label: "Withdrawn" },
  { id: 8, name: "rejected", label: "Rejected" },
];

const STATUS_COLORS: Record<number, string> = {
  1: "bg-slate-100 text-slate-600",
  2: "bg-amber-500/15 text-amber-600",
  3: "bg-emerald-500/15 text-emerald-600",
  4: "bg-cyan-500/15 text-cyan-600",
  5: "bg-sky-500/15 text-sky-600",
  6: "bg-zinc-100 text-zinc-700",
  7: "bg-orange-500/15 text-orange-600",
  8: "bg-rose-500/15 text-rose-600",
  9: "bg-purple-500/15 text-purple-600",
  10: "bg-indigo-500/15 text-indigo-600",
  11: "bg-teal-500/15 text-teal-600",
};

const STATUS_ICONS: Record<number, ReactNode> = {
  1: null,
  2: <Clock className="h-3.5 w-3.5" />,
  3: <CheckCircle2 className="h-3.5 w-3.5" />,
  4: <DollarSign className="h-3.5 w-3.5" />,
  5: <CheckCircle2 className="h-3.5 w-3.5" />,
  6: <XCircle className="h-3.5 w-3.5" />,
  7: <XCircle className="h-3.5 w-3.5" />,
  8: <XCircle className="h-3.5 w-3.5" />,
  9: <DollarSign className="h-3.5 w-3.5" />,
  10: <Clock className="h-3.5 w-3.5" />,
  11: <CheckCircle2 className="h-3.5 w-3.5" />,
};

const PAGE_SIZE = 21;
const FILTERS_STORAGE_KEY = "adminAllListingsFilters";

export default function AdminAllListingsPage() {
  // An org admin sees every listing (shared inventory) but may only edit,
  // delete or read the audit trail of their own organization's — so the
  // actions have to be hidden on everyone else's rows.
  const { user } = useAuth();
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Page lives in the URL (?page=N, 0-based) so that returning from a listing
  // detail via "Back to listings" restores the exact page the admin was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, Number(searchParams.get("page") ?? "0") || 0);
  const setPage = useCallback(
    (next: number) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next > 0) p.set("page", String(next));
          else p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Seed from the same store <ListingFilters> persists to, so returning from a
  // listing detail restores the filters (not just the page). Default to Active.
  const [appliedFilters, setAppliedFilters] = useState<ListingFilterValues>(() =>
    readStoredFilters(FILTERS_STORAGE_KEY, { ...EMPTY_FILTERS, statusId: "3" }),
  );
  const [agents, setAgents] = useState<UserType[]>([]);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [allStates, setAllStates] = useState<LookupItem[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  // Active listings vs the coming-soon deal pipeline — the same two tabs the
  // buyer marketplace (Home) shows, so admins browse both from one page. The
  // shared filter bar and the grid/table toggle drive whichever tab is open.
  // Lives in the URL (?tab=coming-soon) so it survives a listing round-trip and
  // is deep-linkable, exactly like ?page.
  const marketTab: "active" | "coming_soon" =
    searchParams.get("tab") === "coming-soon" ? "coming_soon" : "active";
  const setMarketTab = useCallback(
    (next: "active" | "coming_soon") => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "coming_soon") p.set("tab", "coming-soon");
          else p.delete("tab");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Unfiltered pipeline total for the Coming Soon badge — seeded below and kept
  // fresh by ComingSoonDeals' own unfiltered fetches.
  const [comingSoonTotal, setComingSoonTotal] = useState<number | null>(null);
  // Market explorer pins — the same two sets the buyer marketplace maps.
  const [mapPoints, setMapPoints] = useState<ListingMapPoint[]>([]);
  const [mapPointsLoading, setMapPointsLoading] = useState(true);
  const [comingSoonMapPoints, setComingSoonMapPoints] = useState<ListingMapPoint[]>([]);
  const [comingSoonMapLoading, setComingSoonMapLoading] = useState(true);
  const navigate = useNavigate();
  // Scrolled into view when a coming-soon pin is clicked, so the map hands off
  // to the tab that actually lists the deals.
  const listingsRef = useRef<HTMLDivElement | null>(null);
  const [trendingListings, setTrendingListings] = useState<TrendingListing[]>([]);
  // "Explore by category" tiles — active listing count + cover photo per
  // industry, same source as the buyer marketplace.
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number> | null>(null);
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  // A tile click is pushed into <ListingFilters> so it becomes a normal chip.
  const [categoryPreset, setCategoryPreset] = useState<{ ids: string[]; nonce: number } | undefined>(undefined);
  const [auditListing, setAuditListing] = useState<{ id: string; title: string } | null>(null);
  const toast = useToast();

  // Lookups for resolving names in the result cards/table (the filter UI fetches its own).
  useEffect(() => {
    api.get<UserType[]>("/admin/advisors").then(setAgents).catch(console.error);
    api.get<LookupItem[]>("/lookups/categories").then(setCategories).catch(() => {});
    api.get<LookupItem[]>("/lookups/countries").then(setCountries).catch(() => {});
    api.get<LookupItem[]>("/lookups/states").then(setAllStates).catch(() => {});
    api.get<TrendingListing[]>("/admin/trending-listings?limit=12").then(setTrendingListings).catch(() => {});
    api.get<{ counts: Record<string, number>; images?: Record<string, string> }>("/business-listings/category-counts")
      .then((d) => {
        setCategoryCounts(d.counts);
        setCategoryImages(d.images ?? {});
      })
      .catch(() => {});
    // Count-only endpoint, so the Coming Soon badge has its number before the
    // tab is ever opened.
    api.get<{ total_elements: number }>("/lead-pipeline/count")
      .then((d) => setComingSoonTotal(d.total_elements))
      .catch(() => setComingSoonTotal(0));
    if (user) {
      loadMapPoints("/business-listings/map-points", user.id, setMapPoints, setMapPointsLoading);
      loadMapPoints("/lead-pipeline/map-points", user.id, setComingSoonMapPoints, setComingSoonMapLoading);
    }
    // Keyed on the signed-in user: the pin sets are cached per user id, so a
    // re-run repaints from cache and revalidates in the background.
  }, [user]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildListingFilterParams(appliedFilters);
      params.set("skip", String(page * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      const data = await api.get<{
        total_elements: number;
        total_pages: number;
        current_page: number;
        page_size: number;
        data: BusinessListing[];
      }>(`/admin/listings?${params.toString()}`);
      setListings(data.data);
      setTotal(data.total_elements);
    } catch {
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, toast]);

  // Refetch whenever the applied filters or the page change.
  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  async function deleteListing(listingId: string, title: string) {
    if (!confirm(`Delete "${title}"? You can restore it later from Deleted Listings.`)) return;
    setActionLoading(listingId);
    try {
      await api.delete(`/admin/listings/${listingId}`);
      setListings((prev) => prev.filter((l) => l.id !== listingId));
      setTotal((prev) => prev - 1);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to delete listing");
    } finally {
      setActionLoading(null);
    }
  }

  // Applied filter set comes from the shared <ListingFilters /> component, which
  // also emits once on mount with its initial set. Ignore that echo (and any
  // no-op apply) so it doesn't reset a page restored from the URL; only a real
  // filter change resets back to page 1.
  const onApplyFilters = useCallback((next: ListingFilterValues) => {
    if (
      buildListingFilterParams(next).toString() ===
      buildListingFilterParams(appliedFilters).toString()
    ) {
      return;
    }
    setPage(0);
    setAppliedFilters(next);
  }, [appliedFilters, setPage]);

  const goToPage = (next: number) => setPage(next);

  // Passed as router state on each listing link so ListingDetail's
  // "Back to listings" returns here with the current page preserved.
  const backTo = page > 0 ? `/admin/all-listings?page=${page}` : "/admin/all-listings";

  const statusOptions = LISTING_STATUSES.map((s) => ({ value: String(s.id), label: s.label }));
  const advisorOptions = agents.map((a) => ({
    value: a.id,
    label: `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim() || a.email,
  }));

  const formatShortPrice = (price: number | null) => {
    if (!price) return "-";
    if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(price % 1_000_000 === 0 ? 0 : 1)}m`;
    if (price >= 1_000) return `$${(price / 1_000).toFixed(price % 1_000 === 0 ? 0 : 1)}k`;
    return `$${price}`;
  };

  const getStatusLabel = (id: number) =>
    LISTING_STATUSES.find((s) => s.id === id)?.label || `Status ${id}`;

  return (
    <PageShell>
      {/* Feature banners + trending listings — same as the buyer marketplace. */}
      <HeroCarousel banners={featureBanners(user?.org_slug, true)} />
      <TrendingSection items={trendingListings} flush />

      {/* Market explorer — US state cartogram + pin map, same component and
          Active / Coming Soon pin toggle the buyer marketplace uses. */}
      <MarketExplorer
        points={mapPoints}
        loading={mapPointsLoading}
        onView={(id) => navigate(`/listings/${id}`, { state: { from: backTo } })}
        comingSoonPoints={comingSoonMapPoints}
        comingSoonLoading={comingSoonMapLoading}
        onViewComingSoon={() => {
          // Coming-soon deals aren't listing pages — hand off to the tab that
          // browses them and bring it into view.
          setMarketTab("coming_soon");
          listingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
      />

      {/* Filters */}
      <PagePanel>
        <PanelHeading
          title="Filter listings"
          description="Combine keyword, status, advisor, category, and location filters."
        />
        <ListingFilters
          onApply={onApplyFilters}
          fullWidth
          containerClassName=""
          searchPlaceholder="Search by title or overview..."
          statusOptions={statusOptions}
          initialStatusId="3"
          advisorOptions={advisorOptions}
          storageKey={FILTERS_STORAGE_KEY}
          categoryPreset={categoryPreset}
        />
      </PagePanel>

      {/* Listing results */}
      <PagePanel>
        {/* Category tiles sit above the listings header, as on the buyer marketplace */}
        <CategoryTiles
          categories={categories}
          counts={marketTab === "active" ? categoryCounts : null}
          images={categoryImages}
          selectedIds={appliedFilters.categoryIds}
          onSelect={(id) => setCategoryPreset({ ids: id === null ? [] : [String(id)], nonce: Date.now() })}
        />

        <div ref={listingsRef} className="mb-6 flex flex-wrap items-start justify-between gap-4 scroll-mt-24">
          <div>
            {/* Same wording as the buyer marketplace: heading, combined total,
                then the two tabs. */}
            <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              Available listings
            </h2>
            {/* Combined total — sits between the heading and the tabs, and is
                always the sum of the two badges below it. */}
            <p className="mt-1 text-sm text-muted-foreground">
              Total{" "}
              <span className="font-bold tabular-nums text-primary">
                {loading || comingSoonTotal === null
                  ? "…"
                  : (total + comingSoonTotal).toLocaleString()}
              </span>{" "}
              listings
            </p>
            {/* Active / Coming Soon tabs — mirrors the buyer marketplace. Counts
                are exact numbers; Active follows the applied status filter. */}
            <div className="mt-4 inline-flex rounded-xl border border-border/70 bg-muted p-1">
              <button
                type="button"
                onClick={() => setMarketTab("active")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                  marketTab === "active"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <CircleDot className="h-4 w-4" /> Active
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums",
                    marketTab === "active" ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                  )}
                >
                  {loading && marketTab === "active" ? "…" : total.toLocaleString()}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setMarketTab("coming_soon")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                  marketTab === "coming_soon"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Sparkles className="h-4 w-4" /> Coming Soon
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[0.68rem] font-bold tabular-nums",
                    marketTab === "coming_soon" ? "bg-primary-foreground/15" : "bg-primary/15 text-primary",
                  )}
                >
                  {comingSoonTotal === null ? "…" : comingSoonTotal.toLocaleString()}
                </span>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
            <button
              onClick={() => setViewMode("grid")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                viewMode === "grid"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
              title="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                viewMode === "table"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
              title="Table view"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        {marketTab === "coming_soon" ? (
          /* ── Coming Soon tab — deal pipeline browser, same filters + view toggle ── */
          <ComingSoonDeals
            filters={appliedFilters}
            viewMode={viewMode}
            pageSize={PAGE_SIZE}
            onTotalChange={setComingSoonTotal}
          />
        ) : loading ? (
          <Loader className="min-h-[26vh]" />
        ) : listings.length === 0 ? (
          <EmptyPanel
            icon={Search}
            title="No listings found"
            description="Try broadening your filters or choosing a different status."
          />
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left text-[0.7rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Seller</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Badges</th>
                  <th className="px-4 py-3">Listed</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => {
                  const isActing = actionLoading === listing.id;
                  return (
                    <tr
                      key={listing.id}
                      className={`border-b border-border/40 bg-white transition-colors last:border-0 hover:bg-accent/30 ${
                        isActing ? "pointer-events-none opacity-50" : ""
                      }`}
                    >
                      <td className="max-w-[220px] px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/listings/${listing.id}`}
                            state={{ from: backTo }}
                            className="truncate font-semibold text-foreground hover:text-primary"
                          >
                            {listing.title}
                          </Link>
                          <Link
                            to={`/listings/${listing.id}`}
                            state={{ from: backTo }}
                            className="shrink-0 text-muted-foreground hover:text-primary"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <ListingHotBadge published_at={listing.published_at} size="sm" />
                        </div>
                        <div className="mt-0.5 font-mono text-[0.68rem] text-muted-foreground">
                          {listing.reference_code ?? `${listing.id.slice(0, 8)}...`}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">
                          {listing.seller_name || listing.seller_id.slice(0, 8)}
                        </div>
                        {listing.seller_email && (
                          <div className="text-[0.68rem] text-muted-foreground">
                            {listing.seller_email}
                          </div>
                        )}
                        {listing.agent_name && (
                          <div className="mt-0.5 text-[0.68rem] text-primary">
                            Advisor: {listing.agent_name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-600">
                        {formatShortPrice(listing.asking_price)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {listing.is_sba_approved && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-amber-600">
                              SBA
                            </span>
                          )}
                          {listing.seller_financing && (
                            <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-green-600">
                              Seller Fin.
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(listing.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.12em] ${
                            STATUS_COLORS[listing.listing_status_id] || "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {STATUS_ICONS[listing.listing_status_id]}
                          {getStatusLabel(listing.listing_status_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/listings/${listing.id}`} state={{ from: backTo }}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-primary"
                              title="View listing"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          {canActOnListingOrg(user, listing.org_id) ? (
                            <>
                              <Link to={`/listings/${listing.id}/edit`}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                  title="Edit listing"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setAuditListing({ id: listing.id, title: listing.title })}
                                className="h-7 w-7 text-muted-foreground hover:text-primary"
                                title="Audit history"
                              >
                                <History className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteListing(listing.id, listing.title)}
                                className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                title="Delete listing"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          ) : (
                            /* Another organization administers this listing — the
                               API 403s on edit/history/delete, so don't offer them. */
                            <span
                              className="px-1 text-[0.65rem] font-medium text-muted-foreground/70"
                              title="Managed by another organization"
                            >
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Grid / Card view */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {listings.map((listing) => {
              const isActing = actionLoading === listing.id;
              const imgSrc = listing.listing_image_url ? assetUrl(listing.listing_image_url) : null;
              const categoryName = categories.find((c) => c.id === listing.industry_category_id)?.name;
              const cf = listing.asking_price && listing.ebitda && listing.ebitda !== 0
                ? (listing.asking_price / listing.ebitda).toFixed(2) + "X"
                : null;
              const locationParts = [
                listing.city_name,
                listing.state_name || allStates.find((s) => s.id === listing.state_id)?.name,
                listing.country_name || countries.find((c) => c.id === listing.country_id)?.name,
              ].filter(Boolean);

              return (
                <article
                  key={listing.id}
                  className={`flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 ${
                    isActing
                      ? "pointer-events-none opacity-50"
                      : "hover:-translate-y-0.5 hover:shadow-md"
                  }`}
                >
                  {/* Cover image */}
                  <div className="relative h-44 w-full shrink-0 overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                    {imgSrc ? (
                      <img src={imgSrc} alt={listing.title} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Building2 className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    {/* Category badge */}
                    {categoryName && (
                      <div className="absolute left-3 top-3">
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                          {categoryName}
                        </span>
                      </div>
                    )}
                    {/* Status badge + hot badges */}
                    <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.1em] ${STATUS_COLORS[listing.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_ICONS[listing.listing_status_id]}
                        {getStatusLabel(listing.listing_status_id)}
                      </span>
                      <ListingHotBadge published_at={listing.published_at} size="md" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                          {listing.title}
                        </h3>
                      </div>
                      {listing.reference_code && (
                        <p className="mt-0.5 font-mono text-[0.65rem] tracking-wide text-gray-400">{listing.reference_code}</p>
                      )}
                      {listing.overview && (
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{listing.overview}</p>
                      )}
                    </div>

                    {/* Financial metrics */}
                    <div className="grid grid-cols-2 gap-1.5">
                      {listing.asking_price != null && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Asking</span>
                          <span className="text-xs font-extrabold text-foreground">{formatShortPrice(listing.asking_price)}</span>
                        </div>
                      )}
                      {listing.revenue != null && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">Revenue</span>
                          <span className="text-xs font-extrabold text-foreground">{formatShortPrice(listing.revenue)}</span>
                        </div>
                      )}
                      {listing.ebitda != null && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">EBITDA</span>
                          <span className="text-xs font-extrabold text-foreground">{formatShortPrice(listing.ebitda)}</span>
                        </div>
                      )}
                      {cf && (
                        <div className="flex items-center justify-between rounded-lg border border-border/50 bg-primary/10 px-3 py-2">
                          <span className="text-[0.6rem] font-bold uppercase tracking-wide text-muted-foreground">C.F. Mult.</span>
                          <span className="text-xs font-extrabold text-foreground">{cf}</span>
                        </div>
                      )}
                    </div>

                    {/* Location */}
                    {locationParts.length > 0 && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span>{locationParts.join(", ")}</span>
                      </div>
                    )}

                    {/* Seller */}
                    {listing.seller_name && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="text-xs text-gray-500">
                          Seller: <span className="font-semibold text-gray-800">{listing.seller_name}</span>
                        </span>
                      </div>
                    )}

                    {/* BL Advisor */}
                    {listing.agent_name && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        {listing.agent_avatar_url ? (
                          <img src={assetUrl(listing.agent_avatar_url)!} alt={listing.agent_name} className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                            <Users className="h-3.5 w-3.5 text-primary" />
                          </div>
                        )}
                        <span className="text-xs text-gray-500">
                          BL Advisor: <span className="font-semibold text-gray-800">{listing.agent_name}</span>
                        </span>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="mt-auto flex items-center gap-2 pt-1">
                      <Link to={`/listings/${listing.id}`} state={{ from: backTo }} className="flex-1">
                        <button className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]">
                          View details <ArrowRight className="h-4 w-4" />
                        </button>
                      </Link>
                      {canActOnListingOrg(user, listing.org_id) && (
                        <>
                          <Link to={`/listings/${listing.id}/edit`}>
                            <Button variant="ghost" size="icon" title="Edit" className="text-muted-foreground hover:text-foreground">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setAuditListing({ id: listing.id, title: listing.title })}
                            className="text-muted-foreground hover:text-primary"
                            title="Audit history"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteListing(listing.id, listing.title)}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {marketTab === "active" && !loading && total > 0 && (
          <Pagination
            page={page + 1}
            totalElements={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => goToPage(p - 1)}
          />
        )}
      </PagePanel>

      {/* Audit Trail Drawer */}
      <ListingAuditDrawer
        listingId={auditListing?.id ?? ""}
        listingTitle={auditListing?.title ?? ""}
        open={!!auditListing}
        onClose={() => setAuditListing(null)}
      />
    </PageShell>
  );
}
